/**
 * Google reCAPTCHA v3 for the FFS Contacts forms.
 *
 * A form opts in with `data-recaptcha="<site key>"` — the attribute is only
 * rendered while reCAPTCHA is enabled in FFS Contacts (see functions.php), so an
 * empty/missing key means "skip". The token is sent as `g-recaptcha-response`,
 * the field the plugin verifies.
 *
 * Tokens expire two minutes after they're issued, so one is fetched at submit
 * time rather than on page load — a visitor can easily spend longer than that
 * filling the form in.
 */

export const RECAPTCHA_FIELD = 'g-recaptcha-response'

/**
 * Get a fresh token for `action` (letters, `_` and `/` only — it shows up as a
 * label in the reCAPTCHA admin console). Resolves to '' when the form has no
 * site key; rejects if Google's script never loaded (blocked, offline).
 *
 * @param {string|undefined} siteKey
 * @param {string} action
 * @returns {Promise<string>}
 */
export function getRecaptchaToken(siteKey, action) {
  if (!siteKey) return Promise.resolve('')

  const { grecaptcha } = window
  if (!grecaptcha?.ready) return Promise.reject(new Error('reCAPTCHA is not loaded'))

  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha.execute(siteKey, { action }).then(resolve, reject)
    })
  })
}

/**
 * Classic (full page POST) forms marked `.js-recaptcha-form`: hold the submit,
 * put a token in a hidden field, then submit for real. The submit event only
 * fires once the browser's own validation has passed, and `form.submit()`
 * doesn't re-fire it, so there's no loop.
 */
export function initRecaptchaForms() {
  document.querySelectorAll('.js-recaptcha-form').forEach((form) => {
    const siteKey = form.dataset.recaptcha
    if (!siteKey) return

    let sending = false
    // Coming back via the back button restores this page from bfcache with
    // `sending` still set — clear it so the form can be submitted again.
    window.addEventListener('pageshow', () => {
      sending = false
    })

    form.addEventListener('submit', async (event) => {
      event.preventDefault()
      if (sending) return
      sending = true

      try {
        const token = await getRecaptchaToken(siteKey, form.dataset.recaptchaAction || 'submit')

        let input = form.querySelector(`input[name="${RECAPTCHA_FIELD}"]`)
        if (!input) {
          input = document.createElement('input')
          input.type = 'hidden'
          input.name = RECAPTCHA_FIELD
          form.append(input)
        }
        input.value = token
      } catch {
        // Submit anyway: the server rejects a missing token with a readable
        // message, which beats a button that silently does nothing.
      }

      form.submit()
    })
  })
}
